import React, { useState, useEffect } from 'react';
import { Plus, Save, Settings, Construction, Layers, ChevronDown, Bot } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white outline-none transition-all';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

const DetailsPanel = ({
    selectedUseCaseId,
    formData,
    tabs,
    selectedTabId,
    agents,
    onFormChange,
    onUseCaseSave,
    onAddTab,
    onSelectTab,
    onOpenTabModal,
    onAddAgent,
    onOpenAgentModal,
}) => {
    const [expandedTabIdx, setExpandedTabIdx] = useState(null);

    // Collapse when use case changes
    useEffect(() => {
        setExpandedTabIdx(null);
    }, [selectedUseCaseId]);

    const handleTabToggle = (idx) => {
        if (expandedTabIdx === idx) {
            setExpandedTabIdx(null);
        } else {
            setExpandedTabIdx(idx);
            onSelectTab(idx);
        }
    };

    if (selectedUseCaseId === null) {
        return (
            <div className="col-span-1 md:col-span-7 bg-white rounded border border-gray-100 shadow-sm flex items-center justify-center h-full">
                <div className="text-center px-8">
                    <p className="text-gray-400 text-sm">Select a use case to view and edit details</p>
                </div>
            </div>
        );
    }

    return (
        <div className="col-span-1 md:col-span-7 bg-white rounded border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                        <Construction className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-800 truncate max-w-xs">
                        {formData.title || 'Untitled Use Case'}
                    </h3>
                </div>
                <button
                    onClick={onUseCaseSave}
                    className="px-4 py-1.5 bg-primary hover:bg-primaryDark text-white text-sm rounded transition-colors flex items-center gap-1.5 font-medium"
                >
                    <Save className="w-3.5 h-3.5" /> Save
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* Use Case Form */}
                <div className="px-6 py-5 border-b border-gray-50">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className={labelCls}>Title</label>
                            <input
                                value={formData.title}
                                onChange={(e) => onFormChange('title', e.target.value)}
                                className={inputCls}
                                placeholder="Enter title..."
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Created By</label>
                            <input
                                value={formData.createdBy}
                                onChange={(e) => onFormChange('createdBy', e.target.value)}
                                className={inputCls}
                                placeholder="Enter creator..."
                            />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Description</label>
                        <textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e) => onFormChange('description', e.target.value)}
                            className={`${inputCls} resize-none`}
                            placeholder="Enter description..."
                        />
                    </div>
                </div>

                {/* Tabs Accordion */}
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-primary" />
                            <span className="text-sm font-semibold text-gray-700">Tabs</span>
                            {tabs.length > 0 && (
                                <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-1.5 py-0.5 rounded-full">{tabs.length}</span>
                            )}
                        </div>
                        <button
                            onClick={onAddTab}
                            disabled={!formData.id}
                            className="px-3 py-1.5 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-primary text-xs rounded transition-colors flex items-center gap-1 font-medium"
                        >
                            <Plus className="w-3 h-3" /> Add Tab
                        </button>
                    </div>

                    <div className="space-y-2">
                        {tabs.map((tab, idx) => {
                            const isOpen = expandedTabIdx === idx;
                            return (
                                <div
                                    key={tab.id}
                                    className={`rounded border transition-all duration-200 overflow-hidden ${
                                        isOpen ? 'border-primary/25 shadow-sm' : 'border-gray-100'
                                    }`}
                                >
                                    {/* Tab Header (clickable to expand) */}
                                    <div
                                        onClick={() => handleTabToggle(idx)}
                                        className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                                            isOpen ? 'bg-primary/5' : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOpen ? 'bg-primary' : 'bg-gray-300'}`} />
                                            <div className="min-w-0">
                                                <p className={`text-sm font-medium truncate ${isOpen ? 'text-primary' : 'text-gray-700'}`}>{tab.name}</p>
                                                {tab.description && !isOpen && (
                                                    <p className="text-xs text-gray-400 truncate">{tab.description}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onOpenTabModal(tab); }}
                                                className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                                            >
                                                <Settings className="w-3.5 h-3.5 text-gray-400 hover:text-primary" />
                                            </button>
                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : ''}`} />
                                        </div>
                                    </div>

                                    {/* Agents Dropdown */}
                                    {isOpen && (
                                        <div className="px-4 pb-4 pt-3 bg-white border-t border-gray-100">
                                            <div className="flex items-center justify-between mb-2.5">
                                                <div className="flex items-center gap-1.5">
                                                    <Bot className="w-3.5 h-3.5 text-gray-400" />
                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agents</span>
                                                    {agents.length > 0 && (
                                                        <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-1.5 py-0.5 rounded-full">{agents.length}</span>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onAddAgent(); }}
                                                    className="px-2.5 py-1 hover:bg-gray-100 text-primary text-xs rounded transition-colors flex items-center gap-1 font-medium"
                                                >
                                                    <Plus className="w-3 h-3" /> Add
                                                </button>
                                            </div>

                                            <div className="space-y-1.5">
                                                {agents.map((agent) => (
                                                    <div
                                                        key={agent.id}
                                                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded border border-gray-100 hover:border-gray-200 transition-all"
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${agent.status === 'active' ? 'bg-green-400' : 'bg-gray-300'}`} />
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-gray-800 truncate">{agent.name}</p>
                                                                <p className="text-xs text-gray-400 truncate">{agent.tool_name || 'No tool'}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => onOpenAgentModal(agent)}
                                                            className="p-1.5 rounded hover:bg-primary/10 transition-colors flex-shrink-0 ml-2"
                                                        >
                                                            <Settings className="w-3.5 h-3.5 text-gray-400 hover:text-primary" />
                                                        </button>
                                                    </div>
                                                ))}
                                                {agents.length === 0 && (
                                                    <p className="text-gray-400 text-xs text-center py-3">No agents yet</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {tabs.length === 0 && (
                            <p className="text-gray-400 text-xs text-center py-6">
                                {formData.id ? 'No tabs yet — add one above' : 'Save the use case to add tabs'}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailsPanel;
